// Unified Bluetooth Printer Interface
// Automatically detects platform and routes to the correct implementation:
// - Android Capacitor: Uses cordova-plugin-bluetooth-serial (window.bluetoothSerial)
// - Web Browser: Uses Web Bluetooth API (navigator.bluetooth)

import { Capacitor } from '@capacitor/core';
import {
  listBluetoothDevices as listAndroidDevices,
  checkBluetoothEnabled as checkAndroidEnabled,
  enableBluetooth as enableAndroidBluetooth,
  connectBluetoothDevice as connectAndroidDevice,
  disconnectBluetoothDevice as disconnectAndroidDevice,
  sendBluetoothData as sendAndroidData,
  checkBluetoothPrinter as checkAndroidPrinter,
} from './bluetoothPrinter';
import { getStoredPrinter } from '@/utils/printerStorage';

import {
  webBluetoothPrinter,
  requestWebBluetoothDevice,
  connectWebBluetoothDevice,
  disconnectWebBluetoothDevice,
  sendWebBluetoothData,
  checkWebBluetoothConnection,
  getWebBluetoothDevices,
  isWebBluetoothSupported,
} from './webBluetoothPrinter';

export interface UnifiedBluetoothDevice {
  id?: string;
  name?: string;
  address?: string;
  isPaired?: boolean;
}

/**
 * Platform detection
 */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export function isWebPlatform(): boolean {
  return !Capacitor.isNativePlatform();
}

export function isAndroidPlatform(): boolean {
  return Capacitor.getPlatform() === 'android';
}

/**
 * Check if Bluetooth is supported on the current platform
 */
export async function isBluetoothSupported(): Promise<boolean> {
  if (isNativePlatform()) {
    // Check if bluetoothSerial plugin is available
    return typeof window !== 'undefined' && !!window.bluetoothSerial;
  } else {
    // Check if Web Bluetooth API is available
    return await isWebBluetoothSupported();
  }
}

/**
 * List available Bluetooth devices
 * - Android: Returns list of paired and discoverable devices
 * - Web: Shows browser's native device picker and returns selected device
 */
export async function listBluetoothDevices(): Promise<UnifiedBluetoothDevice[]> {
  if (isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
    console.log('[Unified] Using Android Bluetooth implementation');
    return await listAndroidDevices();
  } else {
    console.log('[Unified] Using Web Bluetooth implementation');
    // Web Bluetooth requires user interaction to show device picker
    // Return empty array - components should call requestDevice() instead
    return await getWebBluetoothDevices();
  }
}

/**
 * Request user to select a Bluetooth device (Web only)
 * On Android, this is not needed as devices are automatically listed
 */
export async function requestBluetoothDevice(): Promise<UnifiedBluetoothDevice | null> {
  if (isWebPlatform()) {
    console.log('[Unified] Requesting Web Bluetooth device...');
    try {
      const device = await requestWebBluetoothDevice();
      return device;
    } catch (error) {
      console.error('[Unified] Error requesting Web Bluetooth device:', error);
      throw error;
    }
  } else {
    console.warn('[Unified] requestBluetoothDevice() is not needed on native platforms');
    return null;
  }
}

/**
 * Check if Bluetooth is enabled
 * - Android: Checks if Bluetooth is enabled in system settings
 * - Web: Always returns true (browser handles Bluetooth state)
 */
export async function checkBluetoothEnabled(): Promise<boolean> {
  if (isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
    console.log('[Unified] Checking Android Bluetooth status');
    return await checkAndroidEnabled();
  } else {
    console.log('[Unified] Web platform - Bluetooth state managed by browser');
    return true; // Browser handles Bluetooth state
  }
}

/**
 * Enable Bluetooth (Android only)
 * - Android: Prompts user to enable Bluetooth
 * - Web: Not applicable (browser manages Bluetooth)
 */
export async function enableBluetooth(): Promise<void> {
  if (isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
    console.log('[Unified] Enabling Android Bluetooth');
    return await enableAndroidBluetooth();
  } else {
    console.log('[Unified] Web platform - Bluetooth is managed by browser');
    // No action needed on web
  }
}

/**
 * Connect to a Bluetooth device
 * - Android: Connects using device address
 * - Web: Connects using device ID (or requests device if not provided)
 */
export async function connectBluetoothDevice(addressOrId?: string): Promise<boolean> {
  if (isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
    console.log('[Unified] Connecting to Android Bluetooth device:', addressOrId);
    if (!addressOrId) {
      throw new Error('Device address is required for Android platform');
    }
    return await connectAndroidDevice(addressOrId);
  } else {
    console.log('[Unified] Connecting to Web Bluetooth device:', addressOrId);
    return await connectWebBluetoothDevice(addressOrId);
  }
}

/**
 * Disconnect from the current Bluetooth device
 */
export async function disconnectBluetoothDevice(): Promise<void> {
  if (isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
    console.log('[Unified] Disconnecting Android Bluetooth device');
    return await disconnectAndroidDevice();
  } else {
    console.log('[Unified] Disconnecting Web Bluetooth device');
    return await disconnectWebBluetoothDevice();
  }
}

/**
 * Send data to the connected Bluetooth printer
 * @param data - String or binary (Uint8Array) data to send to printer
 */
export async function sendBluetoothData(data: string | Uint8Array): Promise<boolean> {
  if (isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
    console.log('[Unified] Sending data via Android Bluetooth');
    return await sendAndroidData(data);
  } else {
    console.log('[Unified] Sending data via Web Bluetooth');
    return await sendWebBluetoothData(data);
  }
}

/**
 * Check if a Bluetooth printer is currently connected
 */
export async function checkBluetoothPrinterConnection(): Promise<{ connected: boolean; error?: string }> {
  if (isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
    console.log('[Unified] Checking Android Bluetooth connection');
    return await checkAndroidPrinter();
  } else {
    console.log('[Unified] Checking Web Bluetooth connection');
    const connected = await checkWebBluetoothConnection();
    return { connected };
  }
}

/**
 * Get the currently connected device info
 */
export function getConnectedDevice(): UnifiedBluetoothDevice | null {
  if (isWebPlatform()) {
    return webBluetoothPrinter.getConnectedDevice();
  }
  // Android doesn't expose connected device info easily
  return null;
}

/**
 * Platform-aware printing function
 * Checks platform and connection status before sending print data
 * @param printContent - Text content (ESC/POS string) to print
 * @param logoData - Optional ESC/POS bitmap data for company logo (sent before text, Android thermal only)
 */
export async function printWithBluetooth(
  printContent: string,
  logoData?: Uint8Array | null
): Promise<{ success: boolean; message?: string }> {
  try {
    console.log('[Unified] Starting Bluetooth print...');
    console.log('[Unified] Platform:', isNativePlatform() ? 'Native' : 'Web');
    console.log('[Unified] Print content length:', printContent.length);
    if (logoData) {
      console.log('[Unified] Logo data length:', logoData.length, 'bytes');
    }

    // Check if Bluetooth is supported
    const supported = await isBluetoothSupported();
    if (!supported) {
      throw new Error('Bluetooth printing is not supported on this platform/browser');
    }

    // Check connection status; on Android try auto-reconnect from stored printer if not connected
    let connectionStatus = await checkBluetoothPrinterConnection();
    if (!connectionStatus.connected && isAndroidPlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
      const stored = getStoredPrinter();
      if (stored?.address) {
        try {
          console.log('[Unified] Not connected, attempting auto-reconnect to stored printer:', stored.address);
          await connectAndroidDevice(stored.address);
          connectionStatus = await checkBluetoothPrinterConnection();
        } catch (e) {
          console.warn('[Unified] Auto-reconnect failed:', e);
        }
      }
    }
    if (!connectionStatus.connected) {
      throw new Error('Bluetooth printer not connected. Please connect to a printer first.');
    }

    // Send logo first (if provided), then text
    if (logoData && logoData.length > 0) {
      await sendBluetoothData(logoData);
      // Brief pause for printer to process bitmap before text
      await new Promise((r) => setTimeout(r, 100));
    }
    const result = await sendBluetoothData(printContent);
    
    if (result) {
      console.log('[Unified] Print successful');
      return { success: true, message: 'Invoice printed successfully' };
    } else {
      throw new Error('Failed to send print data');
    }
  } catch (error: any) {
    console.error('[Unified] Print error:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to print invoice' 
    };
  }
}

// Export platform detection utilities
export { isNativePlatform as isNative, isWebPlatform as isWeb, isAndroidPlatform as isAndroid };

